<template>
  <div class="client-courses" :style="cssProps">
    <header class="client-courses__header">
      <h1 class="client-courses__title">Your courses</h1>
      <p class="client-courses__subtitle">
        The courses your advisor has shared with you.
      </p>
    </header>

    <main class="client-courses__body">
      <!-- Loading -->
      <div v-if="loading" class="client-courses__center">
        <v-progress-circular indeterminate size="48" color="primary" />
      </div>

      <!-- Expired / invalid link -->
      <div v-else-if="error === 'expired'" class="client-courses__center">
        <v-icon size="48" color="grey">bhi-alert</v-icon>
        <h2 class="client-courses__msg-title">This link has expired</h2>
        <p class="client-courses__msg">
          Reply to your most recent message to get a fresh link to your courses.
        </p>
      </div>

      <!-- Generic error -->
      <div v-else-if="error" class="client-courses__center">
        <v-icon size="48" color="grey">bhi-alert</v-icon>
        <h2 class="client-courses__msg-title">Something went wrong</h2>
        <p class="client-courses__msg">Please try opening the link again.</p>
      </div>

      <template v-else>
        <!-- Course detail (steps) -->
        <section v-if="selected" class="client-courses__detail">
          <button class="client-courses__back" type="button" @click="closeDetail">
            &larr; Back to courses
          </button>
          <h2 class="client-courses__detail-title">{{ displayName(selected) }}</h2>
          <p v-if="selected.description" class="client-courses__detail-desc">
            {{ selected.description }}
          </p>

          <div v-if="stepsLoading" class="client-courses__center">
            <v-progress-circular indeterminate size="32" color="primary" />
          </div>
          <ul v-else-if="steps.length" class="client-courses__steps">
            <li v-for="step in steps" :key="step.id" class="client-courses__step">
              <v-icon
                size="20"
                :color="step.done ? 'primary' : 'grey'"
                class="client-courses__step-icon"
              >
                {{ step.done ? 'bhi-check-circle-filled' : 'bhi-ellipse' }}
              </v-icon>
              <div class="client-courses__step-body">
                <span class="client-courses__step-title">{{ step.title }}</span>
                <p v-if="step.description" class="client-courses__step-desc">
                  {{ step.description }}
                </p>
                <a
                  v-if="stepUrl(step)"
                  :href="stepUrl(step)"
                  target="_blank"
                  rel="noopener"
                  class="client-courses__step-link"
                >
                  Open
                </a>
              </div>
            </li>
          </ul>
          <p v-else class="client-courses__msg">No steps in this course yet.</p>
        </section>

        <!-- Course list -->
        <template v-else>
          <!-- Enrolled / Available tabs -->
          <nav class="client-courses__tabs">
            <button
              v-for="tab in tabs"
              :key="tab.status"
              type="button"
              class="client-courses__tab"
              :class="{ 'is-active': tab.status === activeStatus }"
              @click="switchTab(tab.status)"
            >
              {{ tab.label }}
            </button>
          </nav>

          <!-- Empty -->
          <div v-if="!courses.length" class="client-courses__center">
            <v-icon size="48" color="grey">bhi-mortarboard</v-icon>
            <p class="client-courses__msg">
              {{
                activeStatus === 'ACTIVE'
                  ? "You're not enrolled in any courses yet."
                  : 'No courses available right now.'
              }}
            </p>
          </div>

          <!-- Grid of course cards -->
          <div v-else class="client-courses__grid">
            <button
              v-for="course in courses"
              :key="course.id"
              type="button"
              class="client-courses__card"
              :class="{ 'is-new': course.newCourse, 'is-busy': opening === course.id }"
              @click="openCourse(course)"
            >
              <div class="client-courses__thumb">
                <v-progress-circular
                  v-if="opening === course.id"
                  indeterminate
                  size="28"
                  color="primary"
                />
                <img
                  v-else-if="course.thumbnailUrl"
                  :src="course.thumbnailUrl"
                  :alt="displayName(course)"
                  class="client-courses__thumb-img"
                />
                <v-icon v-else size="40" color="primary">bhi-mortarboard</v-icon>
                <span v-if="course.newCourse" class="client-courses__badge">New</span>
              </div>
              <div class="client-courses__meta">
                <span class="client-courses__name">{{ displayName(course) }}</span>
                <p v-if="course.description" class="client-courses__desc">
                  {{ course.description }}
                </p>

                <!-- Enrolled: progress -->
                <template v-if="activeStatus === 'ACTIVE'">
                  <div class="client-courses__progress-track">
                    <div
                      class="client-courses__progress-fill"
                      :style="{ width: progressPct(course) + '%' }"
                    />
                  </div>
                  <span class="client-courses__progress-label">
                    {{ course.done ? 'Completed' : (course.stepsCompleted || 0) + ' / ' + (course.steps || 0) + ' steps' }}
                  </span>
                </template>

                <!-- Available: step count -->
                <span v-else class="client-courses__progress-label">
                  {{ (course.steps || 0) }} {{ (course.steps === 1) ? 'step' : 'steps' }}
                </span>
              </div>
            </button>
          </div>
        </template>
      </template>
    </main>

    <v-snackbar v-model="snackbar" :timeout="4000" bottom>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { apiUnloggedInstance } from '@/api/api.js'
import Config from '@/config.json'
import extendedConfig from '@/tenants.js'

export default {
  name: 'ClientCourses',
  data() {
    return {
      token: null,
      loading: true,
      error: null, // 'expired' | 'generic' | null
      activeStatus: 'ACTIVE', // 'ACTIVE' (enrolled) | 'LIBRARY' (available)
      tabs: [
        { status: 'ACTIVE', label: 'Enrolled' },
        { status: 'LIBRARY', label: 'Available' },
      ],
      courses: [],
      selected: null, // the course whose detail is open
      steps: [],
      stepsLoading: false,
      opening: null, // id of the course currently being opened
      snackbar: false,
      snackbarText: '',
    }
  },
  computed: {
    // /prompt/** and /c/** live at the backend ROOT, but VUE_APP_BACKEND_URL points at .../api.
    // Strip the /api suffix and call an absolute URL so axios ignores the /api baseURL.
    backendRoot() {
      return (Config.VUE_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')
    },
    cssProps() {
      return {
        '--primary': extendedConfig.VUE_CSS_MAIN_COLOR || '#213665',
      }
    },
  },
  created() {
    this.token = this.$route.query.t || null
    if (!this.token) {
      this.loading = false
      this.error = 'expired'
      return
    }
    this.loadCourses()
  },
  methods: {
    displayName(course) {
      return course.title || 'Untitled course'
    },
    progressPct(course) {
      const total = course.steps || 0
      if (!total) return course.done ? 100 : 0
      return Math.min(100, Math.round(((course.stepsCompleted || 0) / total) * 100))
    },
    stepUrl(step) {
      return step.link || step.mediaUrl || step.attachmentUrl || null
    },
    switchTab(status) {
      if (status === this.activeStatus) return
      this.activeStatus = status
      this.loadCourses()
    },
    async loadCourses() {
      this.loading = true
      this.error = null
      try {
        const res = await apiUnloggedInstance().get(
          `${this.backendRoot}/prompt/courses/list`,
          { params: { t: this.token, status: this.activeStatus } },
        )
        this.courses = (res.data && Array.isArray(res.data.content)) ? res.data.content : []
      } catch (e) {
        const status = e && e.response && e.response.status
        this.error = status === 401 || status === 410 ? 'expired' : 'generic'
      } finally {
        this.loading = false
      }
    },
    async openCourse(course) {
      if (this.opening) return
      this.opening = course.id
      try {
        const res = await apiUnloggedInstance().get(
          `${this.backendRoot}/prompt/courses/steps`,
          { params: { t: this.token, courseId: course.id } },
        )
        this.steps = (res.data && Array.isArray(res.data.content)) ? res.data.content : []
        this.selected = course
      } catch (e) {
        const status = e && e.response && e.response.status
        if (status === 401 || status === 410) {
          this.error = 'expired'
        } else {
          this.notify("Couldn't open that course. Please try again.")
        }
      } finally {
        this.opening = null
      }
    },
    closeDetail() {
      this.selected = null
      this.steps = []
    },
    notify(text) {
      this.snackbarText = text
      this.snackbar = true
    },
  },
}
</script>

<style lang="scss" scoped>
.client-courses {
  min-height: 100vh;
  background: #f6f6fa;
  padding: 24px 16px 48px;

  &__header {
    max-width: 960px;
    margin: 0 auto 16px;
  }
  &__title {
    font-size: 24px;
    font-weight: 700;
    color: var(--primary);
    margin: 0;
  }
  &__subtitle {
    color: #52575c;
    margin: 4px 0 0;
    font-size: 14px;
  }
  &__body {
    max-width: 960px;
    margin: 0 auto;
  }
  &__center {
    text-align: center;
    padding: 64px 16px;
    color: #52575c;
  }
  &__msg-title {
    font-size: 18px;
    margin: 16px 0 4px;
    color: #25282b;
  }
  &__msg {
    margin: 0;
    font-size: 14px;
  }
  &__tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }
  &__tab {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 999px;
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #52575c;
    cursor: pointer;
    transition: all 0.15s ease;
    &.is-active {
      background: var(--primary);
      border-color: var(--primary);
      color: #fff;
    }
  }
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
  &__card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 0 0 12px;
    text-align: left;
    cursor: pointer;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }
    &.is-busy {
      opacity: 0.6;
      cursor: progress;
    }
    &.is-new {
      border-color: var(--primary);
    }
  }
  &__thumb {
    position: relative;
    height: 132px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f6f8fb;
    overflow: hidden;
    margin-bottom: 8px;
  }
  &__thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background: var(--primary);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 999px;
  }
  &__meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 12px;
  }
  &__name {
    font-size: 15px;
    font-weight: 700;
    color: #25282b;
  }
  &__desc {
    font-size: 13px;
    color: #52575c;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  &__progress-track {
    height: 6px;
    background: #eceef2;
    border-radius: 999px;
    overflow: hidden;
    margin-top: 4px;
  }
  &__progress-fill {
    height: 100%;
    background: var(--primary);
    border-radius: 999px;
    transition: width 0.2s ease;
  }
  &__progress-label {
    font-size: 12px;
    color: #a0a4a8;
  }
  &__detail {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 20px;
  }
  &__back {
    background: none;
    border: none;
    color: var(--primary);
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    padding: 0;
    margin-bottom: 12px;
    &:hover {
      text-decoration: underline;
    }
  }
  &__detail-title {
    font-size: 20px;
    color: #25282b;
    margin: 0 0 4px;
  }
  &__detail-desc {
    color: #52575c;
    font-size: 14px;
    margin: 0 0 16px;
  }
  &__steps {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  &__step {
    display: flex;
    gap: 12px;
    padding: 12px 0;
    border-top: 1px solid #f0f0f4;
  }
  &__step-icon {
    margin-top: 2px;
  }
  &__step-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  &__step-title {
    font-size: 14px;
    font-weight: 600;
    color: #25282b;
  }
  &__step-desc {
    font-size: 13px;
    color: #52575c;
    margin: 0;
  }
  &__step-link {
    font-size: 13px;
    font-weight: 600;
    color: var(--primary);
    margin-top: 2px;
  }
}
</style>
